import { Controller } from "@hotwired/stimulus"
import "codemirror"
import "codemirror/modes/htmlmixed"
import { show_error, show_success } from "lib/helpers"

export default class extends Controller {
  static targets = [
    "editor",
    "mailType",
    "previewButton",
    "sendTestButton",
  ]

  static values = {
    mailPreviewPath: String,
  }

  connect() {
    const loading = document.getElementById("loading")
    if (loading) loading.style.display = "none"
    const usersPanel = document.getElementById("users")
    if (usersPanel) usersPanel.style.display = ""

    this.boundBeforeCache = () => this.#teardownEditor()
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)

    if (!this.hasEditorTarget) return

    this.editor = CodeMirror.fromTextArea(this.editorTarget, {
      mode: "htmlmixed",
      lineNumbers: true,
    })

    if (this.hasMailTypeTarget) {
      const initialType = this.mailTypeTarget.value
      this.mailTypeTarget.dataset.pre = initialType
      const initialInput = this.#templateInput(initialType)
      this.editor.setValue(initialInput ? initialInput.value : "")
    }

    this.editorChangeHandler = () => this.#updateButtonState()
    this.editor.on("change", this.editorChangeHandler)

    this.#updateButtonState()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.#teardownEditor()
    this.afterAction = null
  }

  onMailTypeChange(event) {
    if (!this.editor) return
    const select = event.currentTarget
    const previousType = select.dataset.pre
    if (previousType) {
      const previousInput = this.#templateInput(previousType)
      if (previousInput) previousInput.value = this.editor.getValue()
    }
    const newType = select.value
    const newInput = this.#templateInput(newType)
    this.editor.setValue(newInput ? newInput.value : "")
    select.dataset.pre = newType
    this.#updateButtonState()
  }

  save(event) {
    if (event) event.preventDefault()
    this.afterAction = "save"
    this.element.requestSubmit()
  }

  preview(event) {
    if (event) event.preventDefault()
    this.afterAction = "preview"
    this.element.requestSubmit()
  }

  sendTest(event) {
    if (event) event.preventDefault()
    this.afterAction = "sendTest"
    this.element.requestSubmit()
  }

  beforeSubmit() {
    if (!this.editor || !this.hasMailTypeTarget) return
    const currentInput = this.#templateInput(this.mailTypeTarget.value)
    if (currentInput) currentInput.value = this.editor.getValue()
  }

  async onSuccess() {
    this.#updateButtonState()
    const action = this.afterAction
    this.afterAction = null

    if (action === "preview") {
      this.#openPreview()
    } else if (action === "sendTest") {
      await this.#sendTestMail()
    }
  }

  #teardownEditor() {
    if (!this.editor) return

    try {
      if (this.editorChangeHandler) {
        this.editor.off("change", this.editorChangeHandler)
      }
      this.editor.toTextArea()
    } catch (_e) {
      // Turbo may already have detached the editor DOM on back navigation.
    }

    this.editor = null
    this.editorChangeHandler = null
  }

  #templateInput(type) {
    if (!type) return null
    return this.element.querySelector(
      `textarea[name="template[${type}][data]"]`,
    )
  }

  #updateButtonState() {
    if (!this.editor) return
    const empty = this.editor.getValue() === ""
    if (this.hasPreviewButtonTarget) {
      this.previewButtonTarget.disabled = empty
    }
    if (this.hasSendTestButtonTarget) {
      this.sendTestButtonTarget.disabled = empty
    }
  }

  #currentType() {
    return this.hasMailTypeTarget ? this.mailTypeTarget.value : ""
  }

  #openPreview() {
    if (!this.mailPreviewPathValue) return
    const url = `${this.mailPreviewPathValue}?type=${encodeURIComponent(this.#currentType())}`
    window.open(url, "_blank")
  }

  async #sendTestMail() {
    if (!this.mailPreviewPathValue) return
    const url = `${this.mailPreviewPathValue}?type=${encodeURIComponent(this.#currentType())}`
    try {
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      })
      let payload = null
      try { payload = await response.json() } catch (_) { payload = null }

      if (response.ok && payload && payload.success) {
        if (typeof show_success === "function") {
          show_success(payload.message || "Test mail sent")
        }
      } else {
        const msg = (payload && payload.message) || "Failed to send test email"
        if (typeof show_error === "function") {
          show_error(msg)
        }
      }
    } catch (_) {
      if (typeof show_error === "function") {
        show_error("Network error. Please try again.")
      }
    }
  }
}
